import { v4 as uuidv4 } from 'uuid';
import type {
  DashboardCalculation,
  DashboardColumnKind,
  DashboardDraft,
  DashboardField,
  DashboardFilter,
  DashboardGroup,
  DashboardWidget,
  DashboardWidgetFormat,
  DashboardWidgetType,
} from '../types';
import { fieldKeyOf, refOf } from '../fieldKeys';
import { defaultOperatorFor, operatorsFor, operatorTakesValue } from '../widgetFilters';
import { applyTypeChange } from '../widgetTypeChange';
import { defaultCalculationFor, wellOf, wellsOf, wellTakesMeasures } from '../widgetTypes';
import {
  WIDGET_COLUMNS,
  WIDGET_FIRST_COLUMN,
  WIDGET_FIRST_ROW,
  WIDGET_DEFAULT_ROWS,
  WIDGET_DEFAULT_SPAN,
  WIDGET_MAX_ROWS,
  WIDGET_MIN_ROWS,
  WIDGET_MIN_SPAN,
} from '../constants';

/**
 * Pure operations over the widgets of a draft (SL-1664, SL-1662).
 *
 * Two families live here because they write the same object from two sides: the
 * panel's, which says **what a widget measures** (`renameWidget`,
 * `setWidgetType`, `setWell`, `setFieldCalculation`), and the canvas's, which
 * says **how much room it takes and where it sits** (`duplicateWidget`,
 * `moveWidget`, `resizeWidget`). Splitting them into two modules would put the
 * two halves of `addWidget` and `deleteWidget` in different files.
 *
 * Same contract as `groupOperations` and `pageOperations`: every function takes
 * the draft first, returns a new one, and returns **the draft itself** when it
 * has nothing to do — never a clone. Callers and tests tell a no-op from a
 * change by identity, so an early return that spreads would break both.
 *
 * Nothing here throws and nothing here reads i18n. A refusal is a no-op, and
 * why a control is off is a code computed by the hook — the same division the
 * rest of the module keeps.
 */

export interface AddWidgetResult {
  draft: DashboardDraft;
  /** Id of the widget just created — the caller makes it the selected one. */
  widgetId: string;
}

/** The widget with this id, or `undefined`. */
export const widgetById = (draft: DashboardDraft, widgetId: string): DashboardWidget | undefined =>
  draft.widgets.find((widget) => widget.id === widgetId);

/** Ids of the widgets of a group, in reading order. */
export const widgetIdsOfGroup = (draft: DashboardDraft, groupId: string): string[] =>
  draft.groups.find((group) => group.id === groupId)?.widgetIds ?? [];

/**
 * Adds an empty widget at the end of a group.
 *
 * **Born empty — no type, no fields** (PRD Feature 3: "Nace vacío (sin
 * campos)"). Seeding it with, say, a KPI would make the canvas show something
 * finished-looking that the user never chose, which is the one thing the PRD
 * rules out: "Nada aparenta estar terminado sin estarlo".
 *
 * The id is minted here, like `addGroup` does, and handed back so the caller
 * can select it. A caller that had to invent the id first could collide with an
 * existing one.
 */
export function addWidget(draft: DashboardDraft, groupId: string): AddWidgetResult {
  const widgetId = uuidv4();
  const widget: DashboardWidget = {
    id: widgetId,
    wells: {},
    // Nothing filtered and nothing formatted, for the same reason there is no
    // type: an empty format is "every default", and writing the defaults in at
    // creation would freeze them — a widget that never chose a palette must
    // follow the day the default palette changes (see `DashboardWidgetFormat`).
    filters: [],
    format: {},
    ...firstFreeRect(draft, groupId, WIDGET_DEFAULT_SPAN, WIDGET_DEFAULT_ROWS),
  };

  return {
    draft: {
      ...draft,
      widgets: [...draft.widgets, widget],
      groups: draft.groups.map((group) =>
        group.id === groupId ? { ...group, widgetIds: [...group.widgetIds, widgetId] } : group,
      ),
    },
    widgetId,
  };
}

/**
 * Adds a widget that arrives already configured at the end of a group.
 *
 * The sibling of `addWidget` for widgets this side did not build: the
 * assistant's (SL-1667). What it measures is kept as proposed; **where it sits
 * is decided here**, with the same `firstFreeRect` a hand-made widget gets —
 * the backend sends no layout, and a proposal that placed widgets itself could
 * land one on top of another.
 *
 * A no-op when the group is missing or the id is already taken, so a stale or
 * repeated proposal cannot duplicate a widget.
 */
export function insertWidget(
  draft: DashboardDraft,
  groupId: string,
  widget: DashboardWidget,
): DashboardDraft {
  if (!draft.groups.some((group) => group.id === groupId)) return draft;
  if (widgetById(draft, widget.id)) return draft;

  const placed: DashboardWidget = {
    ...widget,
    ...firstFreeRect(draft, groupId, WIDGET_DEFAULT_SPAN, WIDGET_DEFAULT_ROWS),
  };

  return {
    ...draft,
    widgets: [...draft.widgets, placed],
    groups: draft.groups.map((group) =>
      group.id === groupId ? { ...group, widgetIds: [...group.widgetIds, widget.id] } : group,
    ),
  };
}

/**
 * Puts what the assistant proposes for a widget in its place (SL-1668).
 *
 * The backend sends a widget whole — type, title, wells, filters — and nothing
 * about where it sits or how it looks. So what it sends replaces what the widget
 * **measures**, and everything the user may have set by hand that the proposal
 * cannot speak to is kept: its rectangle, its format and its description. The
 * id and the place in its group do not change, which is what "modifies it
 * instead of duplicating it" means.
 *
 * A change of type parks the fields the new type has no room for, as switching
 * it by hand does (`applyTypeChange`). Unlike the hand switch, nothing moves to
 * another well (SL-1794): the proposal already says what goes in each one, so a
 * moved field would land nowhere and be lost instead of parked. A no-op when the
 * widget is gone.
 */
export function replaceWidget(
  draft: DashboardDraft,
  widgetId: string,
  proposed: DashboardWidget,
): DashboardDraft {
  const current = widgetById(draft, widgetId);
  if (!current) return draft;

  const parked =
    current.type && proposed.type && proposed.type !== current.type
      ? applyTypeChange(current, proposed.type, { move: false }).parked
      : current.parkedWells;

  // The optional keys are written only when they hold something: absence is
  // what "no title" or "nothing parked" means in this model, never `undefined`.
  const {
    title: _proposedTitle,
    description: _proposedDescription,
    parkedWells: _proposedParked,
    ...measured
  } = proposed;
  const title = proposed.title?.trim();
  const replaced: DashboardWidget = {
    ...measured,
    ...(title ? { title: proposed.title } : {}),
    ...(current.description ? { description: current.description } : {}),
    ...(parked && Object.keys(parked).length > 0 ? { parkedWells: parked } : {}),
    id: current.id,
    column: current.column,
    row: current.row,
    span: current.span,
    rows: current.rows,
    format: current.format,
  };

  return {
    ...draft,
    widgets: draft.widgets.map((widget) => (widget.id === widgetId ? replaced : widget)),
  };
}

/**
 * Renames a widget.
 *
 * A blank title deletes the key instead of storing `''`, the same rule
 * `renameGroup` follows: absence is what "no title" means, and it keeps two
 * equally untitled widgets from serialising differently.
 */
export function renameWidget(
  draft: DashboardDraft,
  widgetId: string,
  title: string,
): DashboardDraft {
  const widget = widgetById(draft, widgetId);
  if (!widget) return draft;

  const renamed: DashboardWidget = { ...widget, title };
  if (!title.trim()) delete renamed.title;

  return {
    ...draft,
    widgets: draft.widgets.map((item) => (item.id === widgetId ? renamed : item)),
  };
}

/**
 * Takes a widget back from `to` to `from`, the way an undo of a type change does
 * (SL-1794) — but only while it is still the widget the undo was offered for: it
 * exists and still has type `to`. Deleted since, or changed again by something
 * else, the draft comes back untouched: undoing then would overwrite a newer
 * decision with an older one.
 */
export function revertWidgetType(
  draft: DashboardDraft,
  widgetId: string,
  from: DashboardWidgetType,
  to: DashboardWidgetType,
): DashboardDraft {
  if (widgetById(draft, widgetId)?.type !== to) return draft;
  return setWidgetType(draft, widgetId, from);
}

/**
 * Changes the widget's visualisation.
 *
 * `wells` holds **only the wells the current type declares**, under the role it
 * declares them. `wells` is keyed by well, and two types can share a key while
 * reading it under a different role (a bar's `xAxis` is a dimension, a scatter's
 * a measure), or not declare it at all. Left in place, such a field would sit
 * inert until a third type read it wrongly — Bar → KPI → Scatter would make the
 * scatter's request refuse (`unsupportedCalculation`). So every field the new
 * type does not accept under the same key and role is **parked**: it moves to
 * `parkedWells` under `${wellKey}:${previousRole}`, and comes back when a type
 * declares that key and role again and the well is empty. Bar → KPI → Bar
 * returns the X axis intact. Extra fields of a multiple → single switch are
 * parked too, so Table → Matrix → Table returns them. The rules live in
 * `applyTypeChange`, shared with `planTypeChange`.
 *
 * `parkedWells` is draft-only and is not serialised: what was discarded is
 * recoverable until the dashboard is saved, and the panel's confirmation
 * (`planTypeChange`) says so. It is absent, not empty, when nothing is parked.
 */
export function setWidgetType(
  draft: DashboardDraft,
  widgetId: string,
  type: DashboardWidgetType,
): DashboardDraft {
  const widget = widgetById(draft, widgetId);
  if (!widget || widget.type === type) return draft;

  const { wells, parked } = applyTypeChange(widget, type);

  const changed: DashboardWidget = { ...widget, type, wells };
  if (Object.keys(parked).length > 0) changed.parkedWells = parked;
  else delete changed.parkedWells;

  return {
    ...draft,
    widgets: draft.widgets.map((item) => (item.id === widgetId ? changed : item)),
  };
}

/**
 * Replaces the fields of one well.
 *
 * The whole well at once and not one field at a time: the panel's control owns
 * the well's contents — add, remove and reorder are all the same edit to it —
 * and an API of `addField`/`removeFieldAt` would put the ordering rules in the
 * caller.
 *
 * A well the current type does not declare is refused as a no-op: the type
 * governs the fields (PRD Feature 4), and writing into a well that does not
 * exist would store something no surface can show or explain.
 *
 * `isNumeric` tells which calculation a bare column starts on — a number opens
 * on Suma, everything else on Conteo. It is a parameter and not a lookup
 * because this module is pure: the schema lives behind react-query, and an
 * operation that reached for it would stop being testable as data. It is asked
 * with the field's **key** (`fieldKeys.ts`), which for the main table is the
 * plain column id.
 *
 * A field keeps its `tableId` (SL-1792): a column of a connected table is the
 * same column only together with its table.
 */
export function setWell(
  draft: DashboardDraft,
  widgetId: string,
  wellKey: string,
  fields: DashboardField[],
  isNumeric: (columnId: string) => boolean = () => false,
): DashboardDraft {
  const widget = widgetById(draft, widgetId);
  if (!widget) return draft;

  const well = wellOf(widget.type, wellKey);
  if (!well) return draft;

  // A single-valued well keeps the last field placed: the control replaces
  // rather than appends, so trimming here matches what the user sees. A capped
  // one (`maxFields`, the Tooltips list) keeps the first ones, as its picker does.
  const next = well.multiple ? fields.slice(0, well.maxFields ?? fields.length) : fields.slice(-1);

  // Measures carry a calculation and dimensions do not. Normalising here rather
  // than in the control is what keeps a field that moved between wells — or one
  // the assistant writes (SL-1668) — from arriving in a shape the well's
  // own rules do not allow.
  const normalised = next.map<DashboardField>(({ columnId, tableId, calculation, part }) => {
    const column = refOf(columnId, tableId);
    if (wellTakesMeasures(well)) {
      return {
        ...column,
        calculation: calculation ?? defaultCalculationFor(isNumeric(fieldKeyOf(column))),
      };
    }
    // AGE-1190: a dimension keeps the part of the cell it groups by; what is sent to the
    // server leaves it out where the visualisation cannot use it (dimensionParts.ts).
    return part ? { ...column, part } : column;
  });

  const wells = { ...widget.wells };
  // An empty well is absent, not an empty array — the same rule `renameGroup`
  // follows with a blank title. Absence is what "nothing here" means, and it
  // keeps two equivalent widgets from serialising differently.
  if (normalised.length === 0) delete wells[wellKey];
  else wells[wellKey] = normalised;

  return {
    ...draft,
    widgets: draft.widgets.map((item) => (item.id === widgetId ? { ...item, wells } : item)),
  };
}

/**
 * Changes the calculation of one measure of a well.
 *
 * Its own operation because it is the edit the Datos section makes most, and
 * routing it through `setWell` would have every calculation change rebuild the
 * well's whole array in the component.
 *
 * The field is named by its key (`fieldKeys.ts`): two tables may share a column id.
 */
export function setFieldCalculation(
  draft: DashboardDraft,
  widgetId: string,
  wellKey: string,
  fieldKey: string,
  calculation: DashboardCalculation,
): DashboardDraft {
  const widget = widgetById(draft, widgetId);
  const fields = widget?.wells[wellKey];
  if (!widget || !fields) return draft;

  // Only where the well takes measures: a dimension has nothing to compute.
  const well = wellOf(widget.type, wellKey);
  if (!well || !wellTakesMeasures(well)) return draft;
  if (!fields.some((field) => fieldKeyOf(field) === fieldKey)) return draft;

  return {
    ...draft,
    widgets: draft.widgets.map((item) =>
      item.id === widgetId
        ? {
            ...item,
            wells: {
              ...item.wells,
              [wellKey]: fields.map((field) =>
                fieldKeyOf(field) === fieldKey ? { ...field, calculation } : field,
              ),
            },
          }
        : item,
    ),
  };
}

/**
 * Removes a widget from the draft and from the group holding it.
 *
 * No confirmation and no guard on the last one, unlike groups and pages:
 * "Borrar: **directo, sin confirmación** — un widget es barato de rehacer y es
 * la operación más frecuente del lienzo" (PRD Feature 3). A group with no
 * widgets is a normal state; a page with no groups is not.
 */
export function deleteWidget(draft: DashboardDraft, widgetId: string): DashboardDraft {
  if (!draft.widgets.some((widget) => widget.id === widgetId)) return draft;

  return {
    ...draft,
    widgets: draft.widgets.filter((widget) => widget.id !== widgetId),
    groups: draft.groups.map((group) =>
      group.widgetIds.includes(widgetId)
        ? { ...group, widgetIds: group.widgetIds.filter((id) => id !== widgetId) }
        : group,
    ),
  };
}

/* ─── The canvas half (SL-1662) ──────────────────────────────────────────────
   A widget is a rectangle on the group's grid, and everything below is
   arithmetic on rectangles: where a new one fits, where a drop puts one, and
   what a resize does to the ones it lands on.

   Two rules hold all of it together. **Nothing overlaps** — whatever the user
   just moved wins its cells and the others are pushed down. And **nothing
   floats back up**: a gap someone left stays, which is the whole reason the row
   is stored instead of being decided by a flow. */

/** A widget's rectangle on the grid, in cells. */
export interface WidgetRect {
  column: number;
  row: number;
  span: number;
  rows: number;
}

/** What a caller is asking a widget to become. Any subset of its rectangle. */
export type WidgetSize = Partial<WidgetRect>;

/** The group holding a widget, or `undefined` when nothing holds it. */
export function groupOfWidget(draft: DashboardDraft, widgetId: string): DashboardGroup | undefined {
  return draft.groups.find((group) => group.widgetIds.includes(widgetId));
}

/** The widgets of a group, in reading order, filtered to the ones that exist. */
export function widgetsOfGroup(draft: DashboardDraft, groupId: string): DashboardWidget[] {
  const byId = new Map(draft.widgets.map((widget) => [widget.id, widget]));
  return widgetIdsOfGroup(draft, groupId)
    .map((widgetId) => byId.get(widgetId))
    .filter((widget): widget is DashboardWidget => Boolean(widget));
}

/** How many rows a group needs to show everything in it. */
function rowsOfGroup(draft: DashboardDraft, groupId: string): number {
  return widgetsOfGroup(draft, groupId).reduce(
    (deepest, widget) => Math.max(deepest, widget.row + widget.rows - 1),
    0,
  );
}

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(Math.round(value), max));

/**
 * Pulls a rectangle back onto the grid.
 *
 * Only the two bounds the grid actually has: twelve columns across, and a first
 * row. Downwards it is open — a group grows to fit what is in it, which is what
 * lets a widget be dragged below everything else and have the group follow.
 *
 * **When a rectangle does not fit, the width is what gives, never the column.**
 * The column is an edge somebody is holding still — the anchor of the resize
 * they are performing — and sliding it left to make a new width fit would move
 * the one thing a resize promises not to move. A widget against the right of the
 * row simply stops widening there.
 *
 * The column is capped at the last one a narrowest widget could start in, so
 * there is always room for `WIDGET_MIN_SPAN` after it.
 */
function onGrid(rect: WidgetRect): WidgetRect {
  const column = clamp(rect.column, WIDGET_FIRST_COLUMN, WIDGET_COLUMNS + 1 - WIDGET_MIN_SPAN);

  return {
    column,
    span: clamp(rect.span, WIDGET_MIN_SPAN, WIDGET_COLUMNS + 1 - column),
    rows: clamp(rect.rows, WIDGET_MIN_ROWS, WIDGET_MAX_ROWS),
    row: Math.max(WIDGET_FIRST_ROW, Math.round(rect.row)),
  };
}

/** Whether two rectangles share any cell. */
function overlaps(a: WidgetRect, b: WidgetRect): boolean {
  return (
    a.column < b.column + b.span &&
    b.column < a.column + a.span &&
    a.row < b.row + b.rows &&
    b.row < a.row + a.rows
  );
}

/**
 * Settles a group after one widget was put somewhere: whatever it lands on moves
 * down, and whatever that lands on moves down too.
 *
 * The one that was just moved never yields — the user placed it deliberately,
 * and a gesture that ended with the widget somewhere else would be a gesture
 * that failed. Everything else is fair game, in reading order, so the same drop
 * always settles the same way.
 *
 * It terminates because every pass only ever moves widgets **down**, and a
 * widget pushed below the deepest one has nothing left to collide with.
 */
function settle(
  widgets: DashboardWidget[],
  groupIds: string[],
  anchorId: string,
): DashboardWidget[] {
  const inGroup = groupIds
    .map((id) => widgets.find((widget) => widget.id === id))
    .filter((widget): widget is DashboardWidget => Boolean(widget));

  const placed: DashboardWidget[] = [];
  const anchor = inGroup.find((widget) => widget.id === anchorId);
  if (anchor) placed.push(anchor);

  for (const widget of inGroup) {
    if (widget.id === anchorId) continue;

    // Against what is already settled, not against the original layout: a
    // widget pushed down can collide with one it used to sit above. Written as
    // a plain scan rather than a closure over `moved`, which the loop reassigns.
    let moved = widget;
    let pushed = true;
    while (pushed) {
      pushed = false;
      for (const other of placed) {
        if (!overlaps(moved, other)) continue;
        moved = { ...moved, row: other.row + other.rows };
        pushed = true;
      }
    }
    placed.push(moved);
  }

  const settled = new Map(placed.map((widget) => [widget.id, widget]));
  return widgets.map((widget) => settled.get(widget.id) ?? widget);
}

/**
 * The reading order of a group, rewritten to match what the eye sees: top to
 * bottom, then left to right.
 *
 * With the layout stored as rectangles, `widgetIds` no longer decides where
 * anything is drawn — but it is still the DOM order, which is the order a screen
 * reader and the tab key follow. Letting it drift from the visual order would
 * make the canvas read in an order nobody can see.
 */
function inReadingOrder(widgets: DashboardWidget[], groupIds: string[]): string[] {
  const byId = new Map(widgets.map((widget) => [widget.id, widget]));
  return [...groupIds].sort((left, right) => {
    const a = byId.get(left);
    const b = byId.get(right);
    if (!a || !b) return 0;
    return a.row - b.row || a.column - b.column;
  });
}

/**
 * The lowest row a widget may start in: one below everything **else** in its
 * group.
 *
 * A group has no bottom of its own — it grows to fit — so without this the last
 * widget could be dragged down for ever, each row taking the group with it. And
 * a group tall enough to fill the screen is one whose neighbour cannot be
 * reached: moving a widget to the group below stops being possible.
 *
 * Measured against the others and not against the widget itself, which is what
 * makes the two cases come out right on one rule. A widget that is alone has
 * nothing under it, so its floor is the first row and dragging it down does
 * nothing at all. One dropped below a stack lands immediately under it, and the
 * group grows by exactly that widget's height — never more.
 */
function lowestRowFor(draft: DashboardDraft, groupId: string, widgetId: string): number {
  const others = widgetsOfGroup(draft, groupId).filter((widget) => widget.id !== widgetId);
  return others.reduce((deepest, widget) => Math.max(deepest, widget.row + widget.rows), 1);
}

/**
 * Where a rectangle would actually end up: on the grid, and no lower than the
 * group allows.
 *
 * Exported because **two** things need the answer and they must not differ. The
 * drop uses it to place the widget; the grid uses it to draw the ghost. A ghost
 * drawn from the raw cell would promise a row the drop then refuses — and worse,
 * it would ask the grid for implicit rows that do not exist, stretching the
 * group under the cursor while the gesture is still in the air.
 */
export function landingRect(
  draft: DashboardDraft,
  widgetId: string,
  groupId: string,
  rect: WidgetRect,
): WidgetRect {
  const onCanvas = onGrid(rect);
  return { ...onCanvas, row: Math.min(onCanvas.row, lowestRowFor(draft, groupId, widgetId)) };
}

/** Writes a widget's rectangle, settles the group around it and re-reads the order. */
function place(
  draft: DashboardDraft,
  widgetId: string,
  groupId: string,
  rect: WidgetRect,
): DashboardDraft {
  const group = draft.groups.find((candidate) => candidate.id === groupId);
  if (!group) return draft;

  const landing = landingRect(draft, widgetId, groupId, rect);

  const moved = draft.widgets.map((widget) =>
    widget.id === widgetId ? { ...widget, ...landing } : widget,
  );
  const widgets = settle(moved, group.widgetIds, widgetId);

  return {
    ...draft,
    widgets,
    groups: draft.groups.map((candidate) =>
      candidate.id === groupId
        ? { ...candidate, widgetIds: inReadingOrder(widgets, candidate.widgetIds) }
        : candidate,
    ),
  };
}

/**
 * The first rectangle of the given size with nothing under it.
 *
 * Scanned left to right and then down, which is where the eye looks for the next
 * free spot. A new widget goes beside the last one while the row has room and
 * starts a new row when it does not — the behaviour a packed flow used to give
 * for free, kept now that the position is explicit.
 */
function firstFreeRect(
  draft: DashboardDraft,
  groupId: string,
  span: number,
  rows: number,
): WidgetRect {
  const taken = widgetsOfGroup(draft, groupId);

  for (
    let row = WIDGET_FIRST_ROW;
    row <= WIDGET_FIRST_ROW + taken.length * WIDGET_MAX_ROWS;
    row += 1
  ) {
    for (let column = WIDGET_FIRST_COLUMN; column + span <= WIDGET_COLUMNS + 1; column += 1) {
      const candidate = { column, row, span, rows };
      if (!taken.some((widget) => overlaps(candidate, widget))) return candidate;
    }
  }

  // Unreachable with the bound above, and cheaper than a `while (true)`: below
  // everything is always free.
  return { column: WIDGET_FIRST_COLUMN, row: rowsOfGroup(draft, groupId) + 1, span, rows };
}

/**
 * Copies a widget and drops the copy in the first free spot of the same group,
 * at the same size and with the same configuration.
 *
 * The first free spot and not "right after the original": with the layout
 * explicit there is no "after", and the copy has to land somewhere it can be
 * seen rather than under what it came from.
 *
 * The wells are copied one level deep, so editing the copy cannot reach into the
 * original's fields — a spread of the widget alone would share the arrays.
 */
export function duplicateWidget(draft: DashboardDraft, widgetId: string): AddWidgetResult {
  const original = widgetById(draft, widgetId);
  const group = groupOfWidget(draft, widgetId);
  if (!original || !group) return { draft, widgetId: '' };

  const copyId = uuidv4();
  const wells = Object.fromEntries(
    Object.entries(original.wells).map(([key, fields]) => [key, [...fields]]),
  );
  const rect = firstFreeRect(draft, group.id, original.span, original.rows);
  const copy: DashboardWidget = { ...original, ...rect, id: copyId, wells };
  // Parked fields travel with the copy: it is the same configuration, and a
  // switch back on the copy must find what a switch back on the original would.
  if (original.parkedWells) {
    copy.parkedWells = Object.fromEntries(
      Object.entries(original.parkedWells).map(([key, fields]) => [key, [...fields]]),
    );
  }

  const withCopy: DashboardDraft = {
    ...draft,
    widgets: [...draft.widgets, copy],
    groups: draft.groups.map((candidate) =>
      candidate.id === group.id
        ? { ...candidate, widgetIds: [...candidate.widgetIds, copyId] }
        : candidate,
    ),
  };

  return { widgetId: copyId, draft: place(withCopy, copyId, group.id, rect) };
}

/**
 * Puts a widget at a rectangle of a given group — the same operation whether it
 * stays where it was or crosses into another one (PRD Feature 3: "it can move
 * between groups", keeping its configuration).
 *
 * One function for both because they are one gesture: the drag does not know,
 * while it is happening, which of the two it will turn out to be. Splitting it
 * would mean the drop handler deciding, and deciding wrong at the boundary.
 *
 * `rect` is what the pointer measured. Without one — the assistant, a keyboard
 * command — the widget keeps its own size and lands in the first free spot of
 * the group it is going to, which is the only honest answer when nobody aimed.
 */
export function moveWidget(
  draft: DashboardDraft,
  widgetId: string,
  toGroupId: string,
  rect?: Partial<WidgetRect>,
): DashboardDraft {
  const widget = widgetById(draft, widgetId);
  const from = groupOfWidget(draft, widgetId);
  const to = draft.groups.find((group) => group.id === toGroupId);
  if (!widget || !from || !to) return draft;

  const crossing = from.id !== to.id;
  const moved: DashboardDraft = crossing
    ? {
        ...draft,
        groups: draft.groups.map((group) => {
          if (group.id === from.id) {
            return { ...group, widgetIds: group.widgetIds.filter((id) => id !== widgetId) };
          }
          if (group.id === to.id) return { ...group, widgetIds: [...group.widgetIds, widgetId] };
          return group;
        }),
      }
    : draft;

  const span = rect?.span ?? widget.span;
  const rows = rect?.rows ?? widget.rows;
  const landing: WidgetRect =
    rect?.column === undefined || rect?.row === undefined
      ? firstFreeRect(crossing ? moved : draft, to.id, span, rows)
      : { column: rect.column, row: rect.row, span, rows };

  return place(moved, widgetId, to.id, landing);
}

/**
 * Adds a filter to a widget, over the column given.
 *
 * The column is chosen by the caller — the section opens the new row on the
 * first column of the table — because this module is pure and the schema lives
 * behind react-query, the same reason `setWell` takes `isNumeric` rather than
 * looking it up.
 *
 * `kind` decides which operator it opens on. A filter is born **without a
 * value**: the user has not written one yet, and seeding it would make a filter
 * that narrows something nobody asked for.
 *
 * `tableId` only for a column of a connected table (SL-1792).
 */
export function addFilter(
  draft: DashboardDraft,
  widgetId: string,
  columnId: string,
  kind: DashboardColumnKind,
  tableId?: string,
): DashboardDraft {
  const widget = widgetById(draft, widgetId);
  if (!widget) return draft;

  const filter: DashboardFilter = {
    id: uuidv4(),
    ...refOf(columnId, tableId),
    operator: defaultOperatorFor(kind),
    value: '',
  };

  return {
    ...draft,
    widgets: draft.widgets.map((item) =>
      item.id === widgetId ? { ...item, filters: [...item.filters, filter] } : item,
    ),
  };
}

/**
 * Replaces one filter.
 *
 * The whole filter at once, not a patch, and that is what the discriminated
 * union buys: a caller cannot hand in an operator without also handing in the
 * value that operator requires, so a filter can never be stored in a shape the
 * evaluator cannot read. A `patch` API would have to re-derive the missing half
 * here and guess wrong exactly when the operator changed.
 */
export function setFilter(
  draft: DashboardDraft,
  widgetId: string,
  filterId: string,
  filter: DashboardFilter,
): DashboardDraft {
  return {
    ...draft,
    widgets: draft.widgets.map((item) =>
      item.id === widgetId
        ? {
            ...item,
            filters: item.filters.map((current) =>
              // The id travels with the row, so a caller that rebuilt the filter
              // cannot accidentally renumber it.
              current.id === filterId ? { ...filter, id: filterId } : current,
            ),
          }
        : item,
    ),
  };
}

/** Removes one filter. */
export function removeFilter(
  draft: DashboardDraft,
  widgetId: string,
  filterId: string,
): DashboardDraft {
  return {
    ...draft,
    widgets: draft.widgets.map((item) =>
      item.id === widgetId
        ? { ...item, filters: item.filters.filter((filter) => filter.id !== filterId) }
        : item,
    ),
  };
}

/**
 * Moves a filter to another column.
 *
 * Its own operation because changing the column is the one edit that can
 * invalidate the rest of the row: the new column's kind may not offer the
 * operator in use, and the value certainly does not survive — `"Monterrey"`
 * means nothing under a numeric `>`.
 *
 * **The operator is kept when the new kind still offers it**, so switching
 * between two text columns does not reset a carefully chosen "starts with". The
 * value is always cleared: a value that matched the old column is a value the
 * user has to look at again, and silently carrying it over is how a filter ends
 * up narrowing by something nobody re-read.
 *
 * `tableId` only for a column of a connected table (SL-1792); moving to a main
 * column drops it.
 */
export function setFilterColumn(
  draft: DashboardDraft,
  widgetId: string,
  filterId: string,
  columnId: string,
  kind: DashboardColumnKind,
  tableId?: string,
): DashboardDraft {
  const widget = widgetById(draft, widgetId);
  if (!widget) return draft;

  const current = widget.filters.find((filter) => filter.id === filterId);
  if (!current) return draft;

  const keeps = operatorsFor(kind).includes(current.operator);
  const operator = keeps ? current.operator : defaultOperatorFor(kind);

  const column = refOf(columnId, tableId);
  const next: DashboardFilter = operatorTakesValue(operator)
    ? { id: filterId, ...column, operator, value: '' }
    : { id: filterId, ...column, operator };

  return setFilter(draft, widgetId, filterId, next);
}

/**
 * Changes a widget's format.
 *
 * A patch and not the whole object, unlike `setFilter`: every property of
 * `DashboardWidgetFormat` is independent and optional, so there is no shape to
 * keep consistent — choosing a palette must not disturb the legend.
 *
 * A property set to `undefined` is **removed**, not stored as undefined. Absent
 * is what "the default" means here, and a key holding `undefined` serialises
 * differently while meaning the same thing.
 */
export function setWidgetFormat(
  draft: DashboardDraft,
  widgetId: string,
  patch: Partial<DashboardWidgetFormat>,
): DashboardDraft {
  return {
    ...draft,
    widgets: draft.widgets.map((item) => {
      if (item.id !== widgetId) return item;

      const format = { ...item.format, ...patch };
      for (const key of Object.keys(patch) as (keyof DashboardWidgetFormat)[]) {
        if (patch[key] === undefined) delete format[key];
      }

      return { ...item, format };
    }),
  };
}

/**
 * Sets a widget's description, the line read next to its title.
 *
 * Blank is **absent**, the same rule `renameWidget` and `renameGroup` follow: a
 * description the user cleared is a widget without one, not a widget with an
 * empty string.
 */
export function setWidgetDescription(
  draft: DashboardDraft,
  widgetId: string,
  description: string,
): DashboardDraft {
  const trimmed = description.trim();

  return {
    ...draft,
    widgets: draft.widgets.map((item) => {
      if (item.id !== widgetId) return item;

      // Built without the key rather than with it deleted: `description` is
      // optional, and a widget that never had one must not start carrying it as
      // `undefined` — the two serialise differently for the same meaning.
      const { description: _dropped, ...rest } = item;
      return trimmed ? { ...rest, description: trimmed } : rest;
    }),
  };
}

/**
 * Resizes a widget, clamped to the grid and settling whatever it grew onto.
 *
 * **The edge the user is not holding does not move.** That is the whole contract
 * of a resize, and it is why this takes a rectangle rather than a width and a
 * height: dragging the top-left corner has to walk `column` and `row` outwards
 * while `column + span` and `row + rows` stay exactly where they were, and only
 * the caller holding the handle knows which two of the four that is.
 *
 * Clamping never moves the anchored edge either. A widget already against the
 * right of the row simply stops widening there; it is not slid left to make the
 * new width fit, which would move the edge the user was holding still.
 */
export function resizeWidget(
  draft: DashboardDraft,
  widgetId: string,
  size: WidgetSize,
): DashboardDraft {
  const widget = widgetById(draft, widgetId);
  const group = groupOfWidget(draft, widgetId);
  if (!widget || !group) return draft;

  return place(draft, widgetId, group.id, {
    column: size.column ?? widget.column,
    row: size.row ?? widget.row,
    span: size.span ?? widget.span,
    rows: size.rows ?? widget.rows,
  });
}
